import React, { useEffect, useState, useMemo } from 'react'
import { Typography, Table, Card, Tag, Space, Spin, DatePicker, Select, Row, Col, Statistic, Alert } from 'antd'
import { BarChartOutlined, CalendarOutlined, TrophyOutlined, TeamOutlined, DollarOutlined } from '@ant-design/icons'
import { apiService } from '../services/apiService'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(isoWeek)

const { Title, Text } = Typography
const { RangePicker } = DatePicker

// Format currency in Indian format (lakhs, crores)
const formatIndianCurrency = (amount: number): string => {
  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(2)}Cr`
  } else if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(2)}L`
  } else if (amount >= 1000) {
    return `₹${(amount / 1000).toFixed(1)}K`
  }
  return `₹${amount.toLocaleString('en-IN')}`
}

interface CadenceDemo {
  sprint_number: string
  batch: string
  date_1: string
  number_of_leads: number | null
  demo_count: number
  revenue: number
}

interface WeeklyDemo {
  week: string
  week_start: string
  demo_count: number
  cadence_count: number
  revenue: number
}

interface AnalyticsData {
  success: boolean
  date_range: {
    start_date: string
    end_date: string
  }
  summary: {
    total_demos_attributed: number
    total_cadences_with_demos: number
    total_demos_in_period: number
    total_revenue: number
  }
  cadences: CadenceDemo[]
  demos_per_week: WeeklyDemo[]
  available_batches: string[]
}

function Analytics() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<AnalyticsData | null>(null)
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ])
  const [batchFilter, setBatchFilter] = useState<string | null>(null)

  const fetchAnalytics = async () => {
    setLoading(true)
    setError(null)
    try {
      let url = `analytics/demo-cadence/?start_date=${dateRange[0].format('YYYY-MM-DD')}&end_date=${dateRange[1].format('YYYY-MM-DD')}`
      if (batchFilter) {
        url += `&batch=${encodeURIComponent(batchFilter)}`
      }

      const response = await apiService.get(url)
      if (response.data.success) {
        setData(response.data)
      } else {
        setError(response.data.error || 'Failed to fetch analytics')
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch analytics data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAnalytics()
  }, [dateRange, batchFilter])

  const cadenceColumns = [
    {
      title: 'Rank',
      key: 'rank',
      width: 70,
      render: (_: any, __: any, index: number) => (
        <Tag color={index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? '#cd7f32' : 'default'}>
          #{index + 1}
        </Tag>
      ),
    },
    {
      title: 'Sprint Number',
      dataIndex: 'sprint_number',
      key: 'sprint_number',
    },
    {
      title: 'Batch',
      dataIndex: 'batch',
      key: 'batch',
      render: (batch: string) => <Tag color="blue">{batch}</Tag>,
    },
    {
      title: 'Schedule Date',
      dataIndex: 'date_1',
      key: 'date_1',
      render: (date: string) => dayjs(date).format('MMM DD, YYYY'),
    },
    {
      title: 'Leads Sent',
      dataIndex: 'number_of_leads',
      key: 'number_of_leads',
      render: (count: number | null) => count ?? '-',
    },
    {
      title: 'Demos',
      dataIndex: 'demo_count',
      key: 'demo_count',
      sorter: (a: CadenceDemo, b: CadenceDemo) => b.demo_count - a.demo_count,
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
    {
      title: 'Conversion',
      key: 'conversion',
      render: (record: CadenceDemo) => {
        if (!record.number_of_leads) return '-'
        const rate = ((record.demo_count / record.number_of_leads) * 100).toFixed(2)
        return <Text type={parseFloat(rate) > 1 ? 'success' : 'secondary'}>{rate}%</Text>
      },
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      sorter: (a: CadenceDemo, b: CadenceDemo) => b.revenue - a.revenue,
      render: (revenue: number) => (
        <Text type={revenue > 0 ? 'success' : 'secondary'} strong={revenue > 0}>
          {revenue > 0 ? formatIndianCurrency(revenue) : '-'}
        </Text>
      ),
    },
  ]

  const weeklyColumns = [
    {
      title: 'Week',
      dataIndex: 'week',
      key: 'week',
      render: (week: string, record: WeeklyDemo) => (
        <Space direction="vertical" size={0}>
          <Text strong>{week}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Starting {dayjs(record.week_start).format('MMM DD')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Demos',
      dataIndex: 'demo_count',
      key: 'demo_count',
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
    {
      title: 'Cadences',
      dataIndex: 'cadence_count',
      key: 'cadence_count',
    },
    {
      title: 'Avg per Cadence',
      key: 'avg',
      render: (record: WeeklyDemo) => (record.demo_count / record.cadence_count).toFixed(1),
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      render: (revenue: number) => (
        <Text type={revenue > 0 ? 'success' : 'secondary'} strong={revenue > 0}>
          {revenue > 0 ? formatIndianCurrency(revenue) : '-'}
        </Text>
      ),
    },
  ]

  if (loading && !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    )
  }

  return (
    <div>
      <Title level={2} style={{ marginBottom: 24 }}>
        <BarChartOutlined /> Demo Analytics
      </Title>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} lg={8}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text type="secondary">Date Range</Text>
              <RangePicker
                value={dateRange}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setDateRange([dates[0], dates[1]])
                  }
                }}
                style={{ width: '100%' }}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Text type="secondary">Batch Filter</Text>
              <Select
                placeholder="All Batches"
                allowClear
                value={batchFilter}
                onChange={setBatchFilter}
                style={{ width: '100%' }}
                options={[
                  { label: 'All Batches', value: null },
                  ...(data?.available_batches || []).map(b => ({ label: b, value: b }))
                ]}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {error && (
        <Alert message={error} type="error" style={{ marginBottom: 24 }} showIcon />
      )}

      {/* Summary Stats */}
      {data && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Demos (Attributed)"
                value={data.summary.total_demos_attributed}
                prefix={<TeamOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Cadences with Demos"
                value={data.summary.total_cadences_with_demos}
                prefix={<CalendarOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Demos in Period"
                value={data.summary.total_demos_in_period}
                prefix={<TrophyOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Revenue"
                value={data.summary.total_revenue}
                prefix={<DollarOutlined />}
                valueStyle={{ color: '#52c41a' }}
                formatter={(value) => formatIndianCurrency(value as number)}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Cadences Ranked by Demo Count */}
      <Card
        title={<><TrophyOutlined /> Cadences Ranked by Demo Count</>}
        style={{ marginBottom: 24 }}
        loading={loading}
      >
        <Table
          dataSource={data?.cadences || []}
          columns={cadenceColumns}
          rowKey={(record) => `${record.sprint_number}-${record.batch}`}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Card>

      {/* Demos Per Week */}
      <Card
        title={<><CalendarOutlined /> Demos Per Week (by Schedule Date)</>}
        loading={loading}
      >
        <Table
          dataSource={data?.demos_per_week || []}
          columns={weeklyColumns}
          rowKey="week"
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  )
}

export default Analytics

